import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import type { ApiEnvelope } from '../models/auth.models';
import type {
  MePayload,
  MePreferencesPatch,
  MePreferencesPayload,
  MeQrCardPayload
} from '../models/me.models';
import { ME_NOTIFICATION_CATEGORIES } from '../models/me.models';
import { environment } from '../../../environments/environment';
import { TokenBagService } from './token-bag.service';
import { resolveTenantBaseUrl } from './your-events.service';

/**
 * MOB-100 Stage 22 — wraps the Stage-8 `/api/mobile/me` baseline plus the
 * NEW Stage-22 `/api/mobile/me/qr-card` and `/api/mobile/me/preferences`
 * endpoints.
 *
 * <ul>
 *   <li><code>load()</code> — refreshes the Stage-8 Me payload (profile,
 *       saved-items summary, leaderboard rank, wallet-pass status, roles).</li>
 *   <li><code>loadQrCard({ force })</code> — fetches the per-attendee
 *       check-in QR. Reads the NEW <code>mobile.AttendeeCheckInQR</code>
 *       table — never <code>registration.CheckInTokens</code>. Re-fetched
 *       on full-screen expand so a server-side revoke surfaces as a
 *       <code>410 Gone</code> with errorCode <code>qr-revoked</code>.</li>
 *   <li><code>loadPreferences()</code> + <code>patchPreferences()</code>
 *       — server-stored haptics + notification-categories (REQ-F-MOB-202).
 *       Mobile reads on app boot, writes on toggle change so the values
 *       sync across the user's devices.</li>
 * </ul>
 */
@Injectable({ providedIn: 'root' })
export class MeService {
  private readonly http = inject(HttpClient);
  private readonly tokenBag = inject(TokenBagService);

  private readonly _payload = signal<MePayload | null>(null);
  private readonly _isLoading = signal(false);
  private readonly _payloadError = signal<string | null>(null);

  private readonly _qrCard = signal<MeQrCardPayload | null>(null);
  private readonly _isLoadingQr = signal(false);
  private readonly _qrError = signal<string | null>(null);
  private readonly _qrRevoked = signal(false);

  private readonly _preferences = signal<MePreferencesPayload | null>(null);
  private readonly _isLoadingPreferences = signal(false);
  private readonly _preferencesError = signal<string | null>(null);

  readonly payload = this._payload.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly payloadError = this._payloadError.asReadonly();

  readonly qrCard = this._qrCard.asReadonly();
  readonly isLoadingQr = this._isLoadingQr.asReadonly();
  readonly qrError = this._qrError.asReadonly();
  readonly qrRevoked = this._qrRevoked.asReadonly();

  readonly preferences = this._preferences.asReadonly();
  readonly isLoadingPreferences = this._isLoadingPreferences.asReadonly();
  readonly preferencesError = this._preferencesError.asReadonly();

  /** True when the Me payload includes a BoothAdmin role assignment. */
  readonly hasBoothAdminRole = computed(() => {
    const roles = this._payload()?.roles ?? [];
    return roles.some((r) => r.toLowerCase() === 'boothadmin');
  });

  /**
   * True when the Me payload includes any organiser-tier role
   * (Organiser / EventAdmin / ClientAdmin / GlobalAdmin / SuperAdmin /
   * Reseller). Drives the OrganiserToolsSection visibility on the Me
   * tab — though the tools themselves remain sudo-gated.
   */
  readonly hasOrganiserRole = computed(() => {
    const roles = (this._payload()?.roles ?? []).map((r) => r.toLowerCase());
    return roles.some((r) =>
      r === 'organiser' ||
      r === 'eventadmin' ||
      r === 'clientadmin' ||
      r === 'globaladmin' ||
      r === 'superadmin' ||
      r === 'reseller'
    );
  });

  // ------------------------------------------------------------------
  // /api/mobile/me  (Stage 8)
  // ------------------------------------------------------------------

  async load(eventId?: number | null): Promise<MePayload | null> {
    const active = this.tokenBag.getActive();
    if (!active) {
      this._payloadError.set('no-active-tenant');
      return null;
    }
    this._isLoading.set(true);
    this._payloadError.set(null);
    try {
      const baseUrl = resolveTenantBaseUrl(environment.apiUrl, active.tenantSlug);
      let params = new HttpParams();
      const resolvedEventId = eventId ?? active.eventId ?? null;
      if (resolvedEventId != null) {
        params = params.set('eventId', String(resolvedEventId));
      }
      const response = await firstValueFrom(
        this.http.get<ApiEnvelope<MePayload>>(`${baseUrl}/api/mobile/me`, {
          params,
          headers: new HttpHeaders({ Accept: 'application/json' })
        })
      );
      const data = response?.data ?? null;
      this._payload.set(data);
      return data;
    } catch (err) {
      this._payloadError.set(stringifyError(err));
      return null;
    } finally {
      this._isLoading.set(false);
    }
  }

  // ------------------------------------------------------------------
  // /api/mobile/me/qr-card  (Stage 22)
  // ------------------------------------------------------------------

  /**
   * Loads the per-attendee QR payload. The `force` flag bypasses the
   * memoised value — used by the full-screen expand handler so a
   * server-side revoke surfaces as `qr-revoked` without waiting for a
   * page navigation.
   */
  async loadQrCard(options: { eventId?: number | null; force?: boolean } = {}): Promise<MeQrCardPayload | null> {
    if (!options.force && this._qrCard() && !this._qrRevoked()) {
      return this._qrCard();
    }
    const active = this.tokenBag.getActive();
    if (!active) {
      this._qrError.set('no-active-tenant');
      return null;
    }
    this._isLoadingQr.set(true);
    this._qrError.set(null);
    try {
      const baseUrl = resolveTenantBaseUrl(environment.apiUrl, active.tenantSlug);
      let params = new HttpParams();
      const resolvedEventId = options.eventId ?? active.eventId ?? null;
      if (resolvedEventId != null) {
        params = params.set('eventId', String(resolvedEventId));
      }
      const response = await firstValueFrom(
        this.http.get<ApiEnvelope<MeQrCardPayload>>(
          `${baseUrl}/api/mobile/me/qr-card`,
          {
            params,
            headers: new HttpHeaders({ Accept: 'application/json' })
          }
        )
      );
      const payload = response?.data ?? null;
      this._qrCard.set(payload);
      this._qrRevoked.set(false);
      return payload;
    } catch (err) {
      const code = readErrorCode(err);
      if (code === 'qr-revoked') {
        this._qrRevoked.set(true);
        this._qrCard.set(null);
        this._qrError.set('qr-revoked');
        return null;
      }
      this._qrError.set(stringifyError(err));
      return null;
    } finally {
      this._isLoadingQr.set(false);
    }
  }

  // ------------------------------------------------------------------
  // /api/mobile/me/preferences  (Stage 22)
  // ------------------------------------------------------------------

  /** Hydrates the local cache from the server. Idempotent. */
  async loadPreferences(): Promise<MePreferencesPayload | null> {
    const active = this.tokenBag.getActive();
    if (!active) {
      this._preferencesError.set('no-active-tenant');
      return null;
    }
    this._isLoadingPreferences.set(true);
    this._preferencesError.set(null);
    try {
      const baseUrl = resolveTenantBaseUrl(environment.apiUrl, active.tenantSlug);
      const response = await firstValueFrom(
        this.http.get<ApiEnvelope<MePreferencesPayload>>(
          `${baseUrl}/api/mobile/me/preferences`,
          { headers: new HttpHeaders({ Accept: 'application/json' }) }
        )
      );
      const payload = ensureCanonicalCategories(response?.data ?? null);
      this._preferences.set(payload);
      return payload;
    } catch (err) {
      this._preferencesError.set(stringifyError(err));
      return null;
    } finally {
      this._isLoadingPreferences.set(false);
    }
  }

  /**
   * Applies a partial patch. Optimistically updates the local cache so
   * the toggle paints immediately; rolls back on HTTP failure.
   */
  async patchPreferences(patch: MePreferencesPatch): Promise<MePreferencesPayload | null> {
    const active = this.tokenBag.getActive();
    if (!active) return null;
    const previous = this._preferences();
    const optimistic: MePreferencesPayload = {
      hapticsEnabled: patch.hapticsEnabled ?? previous?.hapticsEnabled ?? true,
      notificationCategories: {
        ...(previous?.notificationCategories ?? {}),
        ...(patch.notificationCategories ?? {})
      },
      language: patch.language ?? previous?.language ?? null,
      highContrast: patch.highContrast ?? previous?.highContrast ?? false,
      reduceMotion: patch.reduceMotion ?? previous?.reduceMotion ?? false
    };
    this._preferences.set(optimistic);
    try {
      const baseUrl = resolveTenantBaseUrl(environment.apiUrl, active.tenantSlug);
      const response = await firstValueFrom(
        this.http.patch<ApiEnvelope<MePreferencesPayload>>(
          `${baseUrl}/api/mobile/me/preferences`,
          patch,
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
              Accept: 'application/json'
            })
          }
        )
      );
      const merged = ensureCanonicalCategories(response?.data ?? null);
      this._preferences.set(merged);
      return merged;
    } catch (err) {
      // Roll back to the server's last known value.
      this._preferences.set(previous);
      this._preferencesError.set(stringifyError(err));
      throw err;
    }
  }
}

function ensureCanonicalCategories(payload: MePreferencesPayload | null): MePreferencesPayload | null {
  if (!payload) return null;
  // The BFF already serialises the seeded category set, but a defensive
  // merge here means an older build / stripped-down server response
  // still renders every toggle row.
  const categories = { ...payload.notificationCategories };
  for (const key of ME_NOTIFICATION_CATEGORIES) {
    if (!(key in categories)) categories[key] = true;
  }
  return { ...payload, notificationCategories: categories };
}

function readErrorCode(err: unknown): string | null {
  if (err instanceof HttpErrorResponse) {
    const body = err.error as { errorCode?: string } | null;
    if (body && typeof body === 'object' && typeof body.errorCode === 'string') {
      return body.errorCode;
    }
  }
  return null;
}

function stringifyError(err: unknown): string {
  if (!err) return 'unknown-error';
  if (err instanceof HttpErrorResponse) {
    const code = readErrorCode(err);
    if (code) return code;
    return `http-${err.status}`;
  }
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'unknown-error';
}
